import { useCallback, useEffect, useRef } from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createContainer,
	createSubscriber,
	createActionsHook,
	createHook,
	type Action,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type OverrideContainerComponent,
	type SubscriberComponent,
} from '@atlassian/react-sweet-state';
import { actions } from './actions/index.tsx';
import type { State } from './utils.tsx';

type Actions = typeof actions;

type DocumentTitleContainerProps = {
	titleSuffix: string | null;
};

const initialState: State = {
	title: '',
	defaultTitle: '',
	contextArray: [],
};

const Store = createStore<State, Actions>({
	name: 'document-title',
	initialState,
	actions,
});

export const useDocumentTitleActions: HookActionsFunction<
	BoundActions<
		State,
		{
			setTitle: (title: string) => Action<State, DocumentTitleContainerProps>;
			addDocumentTitle: (symbol: symbol, title?: string) => Action<State>;
			removeDocumentTitle: (titleSymbol: symbol) => Action<State>;
			updateTitle: (title: string, context: symbol) => Action<State, DocumentTitleContainerProps>;
		}
	>
> = createActionsHook(Store);
export const useDocumentTitle: HookFunction<
	State,
	BoundActions<
		State,
		{
			setTitle: (title: string) => Action<State, DocumentTitleContainerProps>;
			addDocumentTitle: (symbol: symbol, title?: string) => Action<State>;
			removeDocumentTitle: (titleSymbol: symbol) => Action<State>;
			updateTitle: (title: string, context: symbol) => Action<State, DocumentTitleContainerProps>;
		}
	>,
	void
> = createHook(Store);

export const DocumentTitleContainer: OverrideContainerComponent<DocumentTitleContainerProps> =
	createContainer<State, Actions, DocumentTitleContainerProps>(Store, {
		displayName: 'DocumentTitleContainer',
	});

export const DocumentTitleSubscriber: SubscriberComponent<
	undefined,
	BoundActions<
		State,
		{
			setTitle: (title: string) => Action<State, DocumentTitleContainerProps>;
			addDocumentTitle: (symbol: symbol, title?: string) => Action<State>;
			removeDocumentTitle: (titleSymbol: symbol) => Action<State>;
			updateTitle: (title: string, context: symbol) => Action<State, DocumentTitleContainerProps>;
		}
	>,
	Record<any, any> // oxlint-disable-line @typescript-eslint/no-explicit-any
> = createSubscriber<
	State,
	Actions,
	undefined,
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	Record<any, any>
>(Store, {
	displayName: 'DocumentTitleSubscriber',
});

export const DocumentTitleHelper: React.FC<{ title: string }> = ({ title }) => {
	const { addDocumentTitle, removeDocumentTitle, updateTitle } = useDocumentTitleActions();
	const identifierRef = useRef(Symbol(title));

	const onUpdate = useCallback(() => {
		updateTitle(title, identifierRef.current);
	}, [updateTitle, title]);

	useEffect(() => {
		const titleRef = identifierRef.current;
		addDocumentTitle(titleRef, title);

		return () => {
			removeDocumentTitle(titleRef);
			onUpdate();
		};
	}, [addDocumentTitle, onUpdate, removeDocumentTitle, title]);

	useEffect(() => {
		onUpdate();
	}, [onUpdate]);

	return null;
};
